import { useState, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Mail,
  Loader2,
  Upload,
  Check,
  X,
  Plane,
  Train,
  Bus,
  Ship,
  Hotel,
  Car,
  MapPin,
  CalendarDays,
  FileText,
  Sparkles,
} from "lucide-react";

const TICKET_ICONS = { flight: Plane, train: Train, bus: Bus, ferry: Ship };
const BOOKING_ICONS = { hotel: Hotel, car_rental: Car, apartment: Hotel, hostel: Hotel, resort: Hotel, camping: MapPin };

export const EmailImportButton = ({ mode, onImportTickets, onImportBookings }) => {
  const [open, setOpen] = useState(false);
  const [emailText, setEmailText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setEmailText("");
    setResult(null);
    setSelectedTickets(new Set());
    setSelectedBookings(new Set());
    setParsing(false);
    setImporting(false);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setEmailText(text);
      toast.success("File loaded");
    } catch {
      toast.error("Failed to read file");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const parseEmail = async () => {
    if (!emailText.trim() || emailText.trim().length < 20) {
      toast.error("Please paste a longer email text");
      return;
    }
    setParsing(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/email/parse`,
        { email_text: emailText, parse_type: mode },
        { withCredentials: true }
      );
      setResult(res.data);
      // Select all by default
      const tix = res.data.tickets || [];
      const bkgs = res.data.bookings || [];
      setSelectedTickets(new Set(tix.map((_, i) => i)));
      setSelectedBookings(new Set(bkgs.map((_, i) => i)));
      if (tix.length === 0 && bkgs.length === 0) {
        toast.info("No bookings found in this email");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse email");
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const tickets = (result?.tickets || []).filter((_, i) => selectedTickets.has(i));
      const bookings = (result?.bookings || []).filter((_, i) => selectedBookings.has(i));

      let ticketCount = 0;
      let bookingCount = 0;

      if (tickets.length > 0 && onImportTickets) {
        for (const t of tickets) {
          await onImportTickets(t);
          ticketCount++;
        }
      }
      if (bookings.length > 0 && onImportBookings) {
        for (const b of bookings) {
          await onImportBookings(b);
          bookingCount++;
        }
      }

      const parts = [];
      if (ticketCount > 0) parts.push(`${ticketCount} ticket${ticketCount > 1 ? "s" : ""}`);
      if (bookingCount > 0) parts.push(`${bookingCount} booking${bookingCount > 1 ? "s" : ""}`);
      toast.success(`Imported ${parts.join(" and ")}`);
      handleClose();
    } catch {
      toast.error("Failed to import some items");
    } finally {
      setImporting(false);
    }
  };

  const totalSelected = selectedTickets.size + selectedBookings.size;

  return (
    <>
      <Button
        variant="outline"
        className="btn-outline"
        onClick={() => setOpen(true)}
        data-testid="email-import-btn"
      >
        <Mail className="w-4 h-4 mr-2" />
        Import from Email
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true); }}>
        <DialogContent className="glass-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="email-import-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-sky-400" />
              Import from Email
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            /* Step 1: Paste email */
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-400">
                Paste a booking confirmation email below. AI will extract flight, hotel, and rental details automatically.
              </p>
              <Textarea
                className="glass-input min-h-[200px] text-sm font-mono"
                placeholder={"Paste your booking confirmation email here...\n\nExample:\nDear John,\nYour flight FR1234 from Vienna to Barcelona on March 15, 2026...\nBooking ref: ABC123\nTotal: EUR 89.99"}
                value={emailText}
                onChange={(e) => setEmailText(e.target.value)}
                data-testid="email-text-input"
              />
              <div className="flex gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".txt,.eml,.html,.htm"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  className="btn-outline flex-1"
                  onClick={() => fileRef.current?.click()}
                  data-testid="email-file-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload File
                </Button>
                <Button
                  className="btn-primary flex-1"
                  onClick={parseEmail}
                  disabled={parsing || !emailText.trim()}
                  data-testid="parse-email-btn"
                >
                  {parsing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {parsing ? "Analyzing..." : "Extract Bookings"}
                </Button>
              </div>
            </div>
          ) : (
            /* Step 2: Preview extracted data */
            <div className="space-y-4 py-2">
              {result.summary && (
                <p className="text-sm text-zinc-400 italic" data-testid="parse-summary">
                  {result.summary}
                </p>
              )}

              {/* Tickets */}
              {result.tickets?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-1.5">
                    <Plane className="w-4 h-4 text-sky-400" />
                    Tickets ({result.tickets.length})
                  </h4>
                  <div className="space-y-2">
                    {result.tickets.map((t, i) => {
                      const Icon = TICKET_ICONS[t.type] || Plane;
                      const selected = selectedTickets.has(i);
                      return (
                        <button
                          key={i}
                          onClick={() => setSelectedTickets(prev => {
                            const next = new Set(prev);
                            next.has(i) ? next.delete(i) : next.add(i);
                            return next;
                          })}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selected ? "bg-sky-500/10 border-sky-500/30" : "bg-white/5 border-white/10 opacity-60"
                          }`}
                          data-testid={`parsed-ticket-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center mt-0.5 ${
                              selected ? "bg-sky-500 border-sky-500" : "border-zinc-600"
                            }`}>
                              {selected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-sky-400 flex-shrink-0" />
                                <span className="text-sm font-medium text-white">{t.provider || t.type}</span>
                                {t.booking_reference && (
                                  <span className="text-xs px-1.5 py-0.5 bg-sky-500/20 text-sky-400 rounded">{t.booking_reference}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 mt-1 text-xs text-zinc-400">
                                <MapPin className="w-3 h-3" />
                                {t.departure_location} → {t.arrival_location}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                                {t.departure_time && (
                                  <span className="flex items-center gap-1">
                                    <CalendarDays className="w-3 h-3" />
                                    {t.departure_time}
                                  </span>
                                )}
                                {t.price != null && (
                                  <span>{t.currency || "EUR"} {t.price}</span>
                                )}
                              </div>
                              {t.notes && <p className="text-xs text-zinc-600 mt-1">{t.notes}</p>}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Bookings */}
              {result.bookings?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-1.5">
                    <Hotel className="w-4 h-4 text-emerald-400" />
                    Bookings ({result.bookings.length})
                  </h4>
                  <div className="space-y-2">
                    {result.bookings.map((b, i) => {
                      const Icon = BOOKING_ICONS[b.type] || Hotel;
                      const selected = selectedBookings.has(i);
                      return (
                        <button
                          key={i}
                          onClick={() => setSelectedBookings(prev => {
                            const next = new Set(prev);
                            next.has(i) ? next.delete(i) : next.add(i);
                            return next;
                          })}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selected ? "bg-emerald-500/10 border-emerald-500/30" : "bg-white/5 border-white/10 opacity-60"
                          }`}
                          data-testid={`parsed-booking-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center mt-0.5 ${
                              selected ? "bg-emerald-500 border-emerald-500" : "border-zinc-600"
                            }`}>
                              {selected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                <span className="text-sm font-medium text-white">{b.name || b.provider || b.type}</span>
                                {b.booking_reference && (
                                  <span className="text-xs px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">{b.booking_reference}</span>
                                )}
                              </div>
                              {b.location && (
                                <div className="flex items-center gap-1 mt-1 text-xs text-zinc-400">
                                  <MapPin className="w-3 h-3" />
                                  {b.location}
                                </div>
                              )}
                              <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                                {b.check_in && (
                                  <span className="flex items-center gap-1">
                                    <CalendarDays className="w-3 h-3" />
                                    {b.check_in} → {b.check_out || "?"}
                                  </span>
                                )}
                                {b.price != null && (
                                  <span>{b.currency || "EUR"} {b.price}</span>
                                )}
                              </div>
                              {b.notes && <p className="text-xs text-zinc-600 mt-1">{b.notes}</p>}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {result.tickets?.length === 0 && result.bookings?.length === 0 && (
                <div className="text-center py-6">
                  <FileText className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">No travel bookings found in this email.</p>
                  <Button variant="outline" className="btn-outline mt-3" onClick={reset} data-testid="try-again-btn">
                    Try another email
                  </Button>
                </div>
              )}
            </div>
          )}

          {result && (result.tickets?.length > 0 || result.bookings?.length > 0) && (
            <DialogFooter className="gap-2">
              <Button variant="outline" className="btn-outline" onClick={reset} data-testid="back-to-paste-btn">
                Back
              </Button>
              <Button
                className="btn-primary"
                onClick={handleImport}
                disabled={importing || totalSelected === 0}
                data-testid="confirm-import-btn"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {importing ? "Importing..." : `Import ${totalSelected} item${totalSelected !== 1 ? "s" : ""}`}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
